import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { todayLocalDateString, daysBetween } from '@/utils/dateUtils';
import SessionRow from './SessionRow';
import type { PendingPatient } from './types';

/**
 * Per-patient narrative card used inside PendingNotesBriefing.
 *
 * Renders an avatar, patient name (or initials for HIPAA mode), status badges,
 * a narrative summary line, and an expandable list of sessions via SessionRow.
 */
export default function PatientNarrativeCard({
  patient,
  showFullNames,
  currentEncounterId,
  onNoteClick,
}: {
  patient: PendingPatient;
  showFullNames: boolean;
  currentEncounterId?: string;
  onNoteClick: (encounterId: string) => void;
}) {
  const [expanded, setExpanded] = useState(patient.sessions.length <= 1);
  const hasSessions = patient.sessions.length > 0;
  const hasCurrentSession = patient.sessions.some(
    session => session.encounter_id === currentEncounterId,
  );

  useEffect(() => {
    if (hasCurrentSession) setExpanded(true);
  }, [hasCurrentSession]);

  // Build display name with initials fallback
  const nameParts = patient.patient_name.split(' ');
  const displayName = showFullNames
    ? patient.patient_name
    : nameParts.map(p => p[0]).join('').toUpperCase();

  // Count statuses
  const pendingCount = patient.sessions.filter(s => s.status === 'pending').length;
  const draftedCount = patient.sessions.filter(s => s.status === 'drafted').length;
  const signedCount = patient.sessions.filter(s => s.status === 'signed').length;

  // Determine border color based on most urgent status
  const borderColor = (signedCount > 0 && signedCount === patient.sessions.length)
    ? 'var(--c2, #66b32b)' // all signed
    : draftedCount > 0
      ? 'var(--c4-amber, #f59e0b)' // has drafts
      : 'var(--tile-border, #e5e7eb)'; // pending

  const bgColor = (signedCount > 0 && signedCount === patient.sessions.length)
    ? 'var(--c2-light, rgba(102,179,43,0.06))'
    : draftedCount > 0
      ? 'var(--c4-amber-bg, rgba(245,158,11,0.04))'
      : 'rgba(0,0,0,0.01)';

  // Build narrative summary
  const narrativeParts: string[] = [];
  if (pendingCount > 0) {
    narrativeParts.push(`${pendingCount} ${pendingCount === 1 ? 'note' : 'notes'} to write`);
  }
  if (draftedCount > 0) {
    narrativeParts.push(`${draftedCount} draft${draftedCount === 1 ? '' : 's'} to review`);
  }
  if (signedCount > 0) {
    narrativeParts.push(`${signedCount} completed`);
  }

  // Oldest pending date
  const oldestPending = patient.sessions
    .filter(s => s.status !== 'signed')
    .sort((a, b) => a.date.localeCompare(b.date))[0];

  if (oldestPending) {
    const daysSince = daysBetween(oldestPending.date, todayLocalDateString());
    if (daysSince > 7) {
      narrativeParts.push(`oldest from ${daysSince} days ago`);
    }
  }

  return (
    <Box
      data-testid={`patient-narrative-card-${patient.patient_id}`}
      sx={{
        borderLeft: `3px solid ${borderColor}`,
        bgcolor: bgColor,
        borderRadius: '0 8px 8px 0',
        mb: 0.5,
        overflow: 'hidden',
      }}
    >
      {/* Header row */}
      <Box
        onClick={() => hasSessions && setExpanded(prev => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: 1.5,
          py: 1,
          cursor: hasSessions ? 'pointer' : 'default',
          '&:hover': hasSessions ? { bgcolor: 'rgba(0,0,0,0.02)' } : {},
        }}
      >
        {/* Patient initials avatar */}
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            bgcolor: 'var(--c1-light, #cfeefd)',
            color: 'var(--c1-dark, #2c8db7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.7rem',
            fontWeight: 700,
            flexShrink: 0,
            mt: 0.25,
          }}
        >
          {nameParts.map(p => p[0]).join('').toUpperCase().slice(0, 2)}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Patient name + badges */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: '0.85rem',
                color: 'var(--c1-dark, #2c8db7)',
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                if (patient.sessions[0]) onNoteClick(patient.sessions[0].encounter_id);
              }}
            >
              {displayName}
            </Typography>

            {/* Session count chip */}
            <Chip
              label={`${patient.sessions.length}`}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                bgcolor: pendingCount > 0
                  ? 'var(--c4-amber-light, rgba(245,158,11,0.12))'
                  : 'var(--c2-light, #e2f4d2)',
                color: pendingCount > 0
                  ? 'var(--c4-amber-dark, #b45309)'
                  : 'var(--c2-dark, #3f7e19)',
              }}
            />

            {signedCount > 0 && signedCount === patient.sessions.length && (
              <CheckCircleIcon sx={{ fontSize: 14, color: 'var(--c2, #66b32b)' }} />
            )}
            {hasCurrentSession && (
              <Chip
                label="Working now"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  bgcolor: 'rgba(87,189,228,0.16)',
                  color: 'var(--c1-dark, #2c8db7)',
                }}
              />
            )}
          </Box>

          {/* Narrative summary */}
          {narrativeParts.length > 0 && (
            <Typography variant="body2" sx={{ mt: 0.25, fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.4 }}>
              {narrativeParts.join(' · ')}
            </Typography>
          )}
        </Box>

        {/* Expand toggle */}
        {hasSessions && (
          <Box sx={{ flexShrink: 0, mt: 0.25 }}>
            {expanded ? (
              <ExpandLessIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
            )}
          </Box>
        )}
      </Box>

      {/* Expanded session list */}
      <Collapse in={expanded}>
        <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
          {patient.sessions.map((session) => (
            <SessionRow
              key={session.encounter_id}
              session={session}
              isCurrent={session.encounter_id === currentEncounterId}
              onNoteClick={onNoteClick}
              dashboardContext={session.dashboardContext}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
